import React, { useState } from 'react';
import { func, bool, string, shape } from 'prop-types';
import { Page } from '../../components';
import { heapTrackErrors, heapTrackEvent } from '@shared_modules/baui-heap-tracking';
import getHeaderTitle from './header-title.js';
import { CoBorrowerAddress } from '@shared_modules/baui-ui/components';
import { coBorrowerValidations } from '../../../repeats-redesign/validations';
import useForm from '@shared_modules/baui-ui/hooks/useForm';

const transformAddress = data => {
  const { street, city, state, zip, ...rest } = data;
  return {
    ...rest,
    streetAddress: street,
    city,
    state,
    zip,
  };
};

const getInitialValues = (address, primaryAddress) => {
  const initialAddress = address.secondaryUsePrimaryAddress ? primaryAddress : address;
  return {
    street: initialAddress.streetAddress,
    city: initialAddress.city,
    state: initialAddress.state,
    zip: initialAddress.zip,
    secondaryUsePrimaryAddress: address.secondaryUsePrimaryAddress,
  };
};

const { streetSecondary, citySecondary, zipSecondary, stateSecondary } = coBorrowerValidations;

const customCoBorrowerValidations = {
  street: streetSecondary,
  city: citySecondary,
  state: stateSecondary,
  zip: zipSecondary,
};

const AddressCoBorrower = ({
  primaryName,
  secondaryName,
  address,
  dm,
  isSecondary,
  previousLocation,
  primaryAddress,
  saveValues,
  nextLocation,
}) => {
  const [warningMessages, setWarningMessages] = useState({});
  const [showHelperText, setShowHelperText] = useState({});
  const [hasSeenAndFixedPOWarning, setHasSeenAndFixedPOWarning] = useState(false);

  const submit = data => {
    if (warningMessages.street) {
      heapTrackEvent('PL PI1 Address Page submitted with P.O Box warning', {
        eventValue: 'submitted page with P.O. warning',
      });
    } else if (hasSeenAndFixedPOWarning) {
      // user submits page without P.O warning (after fixing the warning at least once before)
      heapTrackEvent('PL PI1 Address Page submitted without P.O Box warning', {
        eventValue: 'submitted page without P.O. warning after fixing',
      });
    }

    saveValues({
      address: transformAddress(data),
    });
    nextLocation();
  };

  const handleUsePrimaryAddressChange = () => {
    setModel(prevModel => {
      const setValueOrEmpty = value => (prevModel.secondaryUsePrimaryAddress ? '' : value);
      return {
        street: setValueOrEmpty(primaryAddress.streetAddress),
        city: setValueOrEmpty(primaryAddress.city),
        state: setValueOrEmpty(primaryAddress.state),
        zip: setValueOrEmpty(primaryAddress.zip),
        secondaryUsePrimaryAddress: !prevModel.secondaryUsePrimaryAddress,
      };
    });
  };

  const { createSubmitHandler, model, setModel, onChange, onBlur, validationErrors, setValidationErrors } = useForm({
    defaultValues: getInitialValues(address, primaryAddress),
    validations: customCoBorrowerValidations,
  });

  const fnLnAddress = {
    secondaryUsePrimaryAddress: {
      checked: model.secondaryUsePrimaryAddress,
      onChange: handleUsePrimaryAddressChange,
    },
    addressSecondary: {
      defaultValue: {
        streetAddress: model.street,
        state: model.state,
        city: model.city,
        zip: model.zip,
      },
      model,
      onChange,
      onBlur,
      setModel,
      setValidationErrors,
      setShowHelperText,
      setWarningMessages,
      setHasSeenAndFixedPOWarning,
      inputNames: {
        streetAddress: 'street',
        state: 'state',
        city: 'city',
        zip: 'zip',
      },
    },
  };

  const headerTitleProps = {
    primaryName,
    secondaryName,
    secondaryUsePrimaryAddress: model.secondaryUsePrimaryAddress,
    streetAddress: address.streetAddress,
    isExperianDm: dm.isExperianDm,
    isSecondary: isSecondary,
    isDM: dm.isDm,
    hasPartialDMPII: dm.hasPartialDMPII,
  };

  const pageTitleHeader = getHeaderTitle(headerTitleProps);

  return (
    <Page
      title={pageTitleHeader.title}
      header={pageTitleHeader.header}
      name="address"
      goBack={previousLocation}
      submitHandler={createSubmitHandler(submit, errors => {
        heapTrackErrors(errors);
      })}
    >
      <CoBorrowerAddress
        inputs={fnLnAddress}
        validationErrors={validationErrors}
        warningMessages={warningMessages}
        showHelperText={showHelperText}
        onChange={onChange}
        primaryAddress={{ ...primaryAddress, street: primaryAddress.streetAddress }}
        showLabel
      />
    </Page>
  );
};

AddressCoBorrower.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  primaryName: shape({
    firstName: string,
    lastName: string,
  }),
  address: shape({
    streetAddress: string,
    city: string,
    state: string,
    zip: string,
    secondaryUsePrimaryAddress: bool,
  }),
  primaryAddress: shape({
    streetAddress: string,
    city: string,
    state: string,
    zip: string,
  }),
  secondaryName: string,
  isSecondary: bool.isRequired,
  dm: shape({
    isDm: bool,
    isExperianDm: bool,
  }),
};

AddressCoBorrower.defaultProps = {
  primaryName: {
    firstName: '',
    lastName: '',
  },
  address: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    secondaryUsePrimaryAddress: false,
  },
  primaryAddress: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  },
  secondaryName: '',
  dm: {
    isDm: false,
    isExperianDm: false,
  },
};

export default AddressCoBorrower;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/AddressCoBorrower/AddressCoBorrower.scss');